import {jsPDF} from "jspdf";
import {formatGeneratedAt} from "../../digitization/crosssection/reportFormat.js";
import {drawReportLogo} from "../../digitization/crosssection/reportLogo.js";
import {sanitizeFilenameSegment, stripFileExtension} from "../../digitization/crosssection/reportFilename.js";

import {t, T, localized, numberLocale} from "../../digitization/i18n/index.js";
// Inspektionsrapport (POI'er) som PDF, bygget i browseren med jsPDF - samme stil som Graverapport/Tværprofilrapport: A4, titel +
// IT_thirty four-logo, metadata, en oversigt (antal pr. status og en tabel over alle POI'er) og derefter ét afsnit pr. POI med
// alt indholdet fra kortets tre faner: Detaljer (felter, beskrivelse), Billeder (skærmbillede, billeder fra punktskyen, egne fotos) og
// Sted & link (position, punktsky, link). Bygningen kender ingen lagring: værten leverer POI'erne med billeder som data-URL'er.
//
//   Potree.buildPoiReportPdf(pois, {name, project, coordinateSystem, filterLabel, statuses}) -> {filename}   (gemmer filen)
//
//   poi = {number, title, category, status, description, assignee, dueDate (YYYY-MM-DD), createdBy, createdAt (ISO),
//          position: {x, y, z}, pointcloudName, link (URL til POI'en i PointView), geonoteRef,
//          screenshot: {dataUrl, width?, height?} | null,
//          sources: [{dataUrl, name, caption, width?, height?}]   (billeder fra punktskyens dannelse)
//          photos: [{dataUrl, name, width?, height?}]             (brugerens egne fotos)
//   statuses = {<id>: {label, color: [r, g, b]}}  (default: open / in_progress / resolved)

const PAGE_WIDTH = 210, PAGE_HEIGHT = 297;
const MARGIN = 14;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const FOOTER_BASELINE_Y = PAGE_HEIGHT - 8;
const FOOTER_RULE_Y = PAGE_HEIGHT - 12;
const BOTTOM_LIMIT = PAGE_HEIGHT - 18;
const BLUE = [47, 143, 214];

export const DEFAULT_STATUSES = localized({
	open: {label: T("Åben"), color: [226, 75, 74]},
	in_progress: {label: T("I gang"), color: [55, 138, 221]},
	resolved: {label: T("Løst"), color: [99, 153, 34]},
});

const FALLBACK_STATUS = localized({label: T("Ukendt"), color: [136, 135, 128]});

function statusOf(statuses, id){
	return (statuses && statuses[id]) || DEFAULT_STATUSES[id] || {label: id || FALLBACK_STATUS.label, color: FALLBACK_STATUS.color};
}

export function formatDate(iso){
	if(!iso) return "";
	const date = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
	if(Number.isNaN(date.getTime())) return String(iso);
	return date.toLocaleDateString(numberLocale(), {day: "numeric", month: "long", year: "numeric"});
}

export function isOverdue(poi, now = new Date()){
	if(!poi.dueDate || poi.status === "resolved") return false;
	const due = new Date(`${poi.dueDate}T23:59:59`);
	return !Number.isNaN(due.getTime()) && due < now;
}

export function poiReportFilename(name){
	if(!name) return t("Inspektionsrapport.pdf");
	const base = sanitizeFilenameSegment(stripFileExtension(name));
	return base ? t("Inspektionsrapport_{0}.pdf", base) : t("Inspektionsrapport.pdf");
}

function imageFormat(dataUrl){
	return /^data:image\/png/i.test(dataUrl) ? "PNG" : "JPEG";
}

function imageSize(doc, image){
	if(image.width > 0 && image.height > 0) return {width: image.width, height: image.height};
	const props = doc.getImageProperties(image.dataUrl);
	return {width: props.width, height: props.height};
}

function fitImage(doc, image, maxWidth, maxHeight){
	const size = imageSize(doc, image);
	const ratio = Math.min(maxWidth / size.width, maxHeight / size.height);
	return {width: size.width * ratio, height: size.height * ratio};
}

function fieldLine(doc, x, y, label, value, width){
	doc.setFont(undefined, "normal");
	doc.setFontSize(7.5);
	doc.setTextColor(120, 120, 120);
	doc.text(label.toUpperCase(), x, y);
	doc.setFont(undefined, "bold");
	doc.setFontSize(10);
	doc.setTextColor(30, 30, 30);
	doc.text(doc.splitTextToSize(String(value), width)[0] || "", x, y + 4.3);
}

function drawFooters(doc){
	const pageCount = doc.internal.getNumberOfPages();
	for(let p = 1; p <= pageCount; p++){
		doc.setPage(p);
		doc.setDrawColor(225, 225, 225);
		doc.setLineWidth(0.2);
		doc.line(MARGIN, FOOTER_RULE_Y, PAGE_WIDTH - MARGIN, FOOTER_RULE_Y);
		doc.setFont(undefined, "normal");
		doc.setFontSize(8);
		doc.setTextColor(140, 140, 140);
		doc.text(t("PointView · Inspektionsrapport"), MARGIN, FOOTER_BASELINE_Y);
		doc.text(t("Side {0} af {1}", p, pageCount), PAGE_WIDTH - MARGIN, FOOTER_BASELINE_Y, {align: "right"});
	}
}

function pageHeader(doc, title, subtitle, tagline){
	const y = MARGIN;
	doc.setFont(undefined, "bold");
	doc.setFontSize(20);
	doc.setTextColor(15, 15, 15);
	doc.text(title, MARGIN, y + 7);
	doc.setFont(undefined, "normal");
	doc.setFontSize(11);
	doc.setTextColor(70, 70, 70);
	doc.text(subtitle, MARGIN, y + 13);
	const logoX = drawReportLogo(doc, PAGE_WIDTH, MARGIN, y);
	if(tagline){
		doc.setFontSize(8.5);
		doc.setTextColor(130, 130, 130);
		doc.text(tagline, logoX - 4, y + 9, {align: "right"});
	}
	doc.setDrawColor(...BLUE);
	doc.setLineWidth(0.8);
	doc.line(MARGIN, y + 17, PAGE_WIDTH - MARGIN, y + 17);
	return y + 24;
}

function drawStatusDot(doc, x, y, color, radius, filled = true){
	doc.setDrawColor(...color);
	doc.setFillColor(...color);
	doc.setLineWidth(0.9);
	if(filled) doc.circle(x, y, radius, "F"); else doc.circle(x, y, radius, "S");
}

function drawNumberBadge(doc, x, y, number, color){
	doc.setDrawColor(...color);
	doc.setLineWidth(1.1);
	doc.setFillColor(255, 255, 255);
	doc.circle(x, y, 4.2, "FD");
	doc.setFont(undefined, "bold");
	doc.setFontSize(9);
	doc.setTextColor(30, 30, 30);
	doc.text(String(number), x, y + 1.2, {align: "center"});
}

// Oversigt: antal pr. status (+ forfaldne) og en tabel over alle POI'er
function drawOverview(doc, pois, statuses, y, now){
	const order = [...new Set([...Object.keys(statuses || DEFAULT_STATUSES), ...pois.map((p) => p.status)])];
	let x = MARGIN;
	for(const id of order){
		const count = pois.filter((p) => p.status === id).length;
		if(count === 0 && !(statuses || DEFAULT_STATUSES)[id]) continue;
		const status = statusOf(statuses, id);
		drawStatusDot(doc, x + 2.5, y - 1, status.color, 2.2);
		doc.setFont(undefined, "bold");
		doc.setFontSize(12);
		doc.setTextColor(30, 30, 30);
		doc.text(String(count), x + 7, y);
		doc.setFont(undefined, "normal");
		doc.setFontSize(9);
		doc.setTextColor(100, 100, 100);
		doc.text(status.label, x + 7 + doc.getTextWidth(String(count)) + 2, y);
		x += 38;
	}
	const overdue = pois.filter((p) => isOverdue(p, now)).length;
	if(overdue > 0){
		doc.setFont(undefined, "bold");
		doc.setFontSize(9);
		doc.setTextColor(163, 45, 45);
		doc.text(t("{0} forfaldne", overdue), PAGE_WIDTH - MARGIN, y, {align: "right"});
	}
	y += 8;

	const columns = [
		{title: t("Nr"), x: MARGIN, width: 10},
		{title: t("Titel"), x: MARGIN + 11, width: 70},
		{title: t("Kategori"), x: MARGIN + 83, width: 28},
		{title: t("Status"), x: MARGIN + 113, width: 22},
		{title: t("Ansvarlig"), x: MARGIN + 137, width: 26},
		{title: t("Frist"), x: MARGIN + 165, width: 17},
	];
	const header = () => {
		doc.setFont(undefined, "bold");
		doc.setFontSize(7.5);
		doc.setTextColor(120, 120, 120);
		for(const c of columns) doc.text(c.title.toUpperCase(), c.x, y);
		doc.setDrawColor(210, 210, 210);
		doc.setLineWidth(0.2);
		doc.line(MARGIN, y + 1.8, PAGE_WIDTH - MARGIN, y + 1.8);
		y += 6;
	};
	header();
	for(const poi of pois){
		if(y > BOTTOM_LIMIT - 4){
			doc.addPage();
			y = MARGIN + 6;
			header();
		}
		const status = statusOf(statuses, poi.status);
		doc.setFont(undefined, "bold");
		doc.setFontSize(9);
		doc.setTextColor(30, 30, 30);
		doc.text(String(poi.number), columns[0].x, y);
		doc.setFont(undefined, "normal");
		const cells = [
			doc.splitTextToSize(poi.title || t("(uden titel)"), columns[1].width)[0],
			doc.splitTextToSize(poi.category || "", columns[2].width)[0],
			null,
			doc.splitTextToSize(poi.assignee || "", columns[4].width)[0],
			poi.dueDate ? new Date(`${poi.dueDate}T12:00:00`).toLocaleDateString(numberLocale(), {day: "numeric", month: "short"}) : "",
		];
		doc.text(cells[0] || "", columns[1].x, y);
		doc.setTextColor(90, 90, 90);
		doc.text(cells[1] || "", columns[2].x, y);
		drawStatusDot(doc, columns[3].x + 1.6, y - 1, status.color, 1.5);
		doc.setTextColor(30, 30, 30);
		doc.text(status.label, columns[3].x + 5, y);
		doc.setTextColor(90, 90, 90);
		doc.text(cells[3] || "", columns[4].x, y);
		if(isOverdue(poi, now)) doc.setTextColor(163, 45, 45);
		doc.text(cells[4] || "", columns[5].x, y);
		doc.setDrawColor(238, 238, 238);
		doc.line(MARGIN, y + 2, PAGE_WIDTH - MARGIN, y + 2);
		y += 6.2;
	}
	return y;
}

function ensureSpace(doc, y, needed){
	if(y + needed > BOTTOM_LIMIT){
		doc.addPage();
		return MARGIN + 6;
	}
	return y;
}

// lille overskrift for en af kortets faner
function sectionLabel(doc, y, text){
	y = ensureSpace(doc, y, 14);
	doc.setFont(undefined, "bold");
	doc.setFontSize(8);
	doc.setTextColor(...BLUE);
	doc.text(text.toUpperCase(), MARGIN, y);
	doc.setDrawColor(225, 225, 225);
	doc.setLineWidth(0.2);
	doc.line(MARGIN, y + 1.6, PAGE_WIDTH - MARGIN, y + 1.6);
	return y + 6;
}

// felt med etiket og værdi, hvor værdien må fylde flere linjer (og evt. er et link)
function textBlock(doc, y, label, value, {url} = {}){
	const lines = doc.splitTextToSize(String(value), CONTENT_WIDTH);
	y = ensureSpace(doc, y, 6 + Math.min(lines.length, 3) * 4);
	doc.setFont(undefined, "normal");
	doc.setFontSize(7.5);
	doc.setTextColor(120, 120, 120);
	doc.text(label.toUpperCase(), MARGIN, y);
	y += 4.3;
	doc.setFontSize(url ? 8 : 10);
	for(const line of lines){
		y = ensureSpace(doc, y, 5);
		if(url){
			doc.setTextColor(...BLUE);
			doc.textWithLink(line, MARGIN, y, {url});
		}else{
			doc.setFont(undefined, "bold");
			doc.setTextColor(30, 30, 30);
			doc.text(line, MARGIN, y);
		}
		y += url ? 3.8 : 4.6;
	}
	return y + 1.5;
}

// billeder i et gitter med tre kolonner; caption (valgfri) står under hvert billede; scale < 1 gør billederne lavere (så en POI kan blive på én side)
function drawImages(doc, images, y, scale = 1){
	const cols = 3, gap = 3, cell = (CONTENT_WIDTH - gap * (cols - 1)) / cols, imageHeight = cell * 0.75 * scale;
	for(let i = 0; i < images.length; i++){
		const col = i % cols;
		const rowHeight = imageHeight + (images.some((im) => im.caption) ? 4 : 0);
		if(col === 0) y = ensureSpace(doc, y, rowHeight);
		const box = fitImage(doc, images[i], cell * scale, imageHeight);
		const x = MARGIN + col * (cell + gap) + (cell - box.width) / 2;
		doc.addImage(images[i].dataUrl, imageFormat(images[i].dataUrl), x, y + (imageHeight - box.height) / 2, box.width, box.height, undefined, "FAST");
		if(images[i].caption){
			doc.setFont(undefined, "normal");
			doc.setFontSize(7);
			doc.setTextColor(120, 120, 120);
			doc.text(images[i].caption, MARGIN + col * (cell + gap), y + imageHeight + 3);
		}
		if(col === cols - 1 || i === images.length - 1) y += rowHeight + gap;
	}
	return y;
}

// ét afsnit pr. POI med indholdet fra kortets tre faner. Tegnes fra toppen af en ny side; scale (< 1) gør billederne mindre.
function drawPoi(doc, poi, statuses, y, now, scale = 1){
	const status = statusOf(statuses, poi.status);
	const hasShot = !!(poi.screenshot && poi.screenshot.dataUrl);
	drawNumberBadge(doc, MARGIN + 4.4, y + 3, poi.number, status.color);
	doc.setFont(undefined, "bold");
	doc.setFontSize(13);
	doc.setTextColor(15, 15, 15);
	doc.text(doc.splitTextToSize(poi.title || t("(uden titel)"), CONTENT_WIDTH - 40)[0], MARGIN + 11, y + 2.4);
	doc.setFont(undefined, "normal");
	doc.setFontSize(9);
	doc.setTextColor(status.color[0] * 0.8, status.color[1] * 0.8, status.color[2] * 0.8);
	doc.text(status.label, PAGE_WIDTH - MARGIN, y + 2.4, {align: "right"});
	doc.setTextColor(110, 110, 110);
	const sub = [poi.createdBy ? t("oprettet af {0}", poi.createdBy) : null, poi.createdAt ? formatDate(poi.createdAt) : null].filter(Boolean).join(" · ");
	if(sub) doc.text(sub, MARGIN + 11, y + 7.4);
	y += 13;

	// --- Detaljer ---
	y = sectionLabel(doc, y, t("Detaljer"));
	const fields = [
		[t("Status"), status.label],
		poi.category ? [t("Kategori"), poi.category] : null,
		poi.assignee ? [t("Ansvarlig"), poi.assignee] : null,
		poi.dueDate ? [t("Frist"), formatDate(poi.dueDate) + (isOverdue(poi, now) ? " " + t("(forfalden)") : "")] : null,
	].filter(Boolean);
	const colWidth = CONTENT_WIDTH / 2;
	fields.forEach(([label, value], i) => fieldLine(doc, MARGIN + (i % 2) * colWidth, y + Math.floor(i / 2) * 10, label, value, colWidth - 4));
	y += Math.ceil(fields.length / 2) * 10 + 1;
	if(poi.description){
		doc.setFont(undefined, "normal");
		doc.setFontSize(9.5);
		doc.setTextColor(60, 60, 60);
		for(const line of doc.splitTextToSize(poi.description, CONTENT_WIDTH)){
			y = ensureSpace(doc, y, 5);
			doc.text(line, MARGIN, y + 2);
			y += 4.4;
		}
		y += 3;
	}

	// --- Billeder ---
	const sources = (poi.sources || []).filter((p) => p && p.dataUrl);
	const photos = (poi.photos || []).filter((p) => p && p.dataUrl);
	if(hasShot || sources.length || photos.length){
		y = sectionLabel(doc, y, t("Billeder"));
		if(hasShot){
			const box = fitImage(doc, poi.screenshot, CONTENT_WIDTH, 78 * scale);
			y = ensureSpace(doc, y, box.height + 4);
			const x = MARGIN + (CONTENT_WIDTH - box.width) / 2;
			doc.addImage(poi.screenshot.dataUrl, imageFormat(poi.screenshot.dataUrl), x, y, box.width, box.height, undefined, "FAST");
			doc.setDrawColor(200, 200, 200);
			doc.setLineWidth(0.2);
			doc.rect(x, y, box.width, box.height);
			y += box.height + 4;
		}
		if(sources.length){
			doc.setFont(undefined, "normal");
			doc.setFontSize(8);
			doc.setTextColor(110, 110, 110);
			y = ensureSpace(doc, y, 8);
			doc.text(t("Billeder fra punktskyen"), MARGIN, y);
			y = drawImages(doc, sources, y + 3, scale);
		}
		if(photos.length){
			doc.setFont(undefined, "normal");
			doc.setFontSize(8);
			doc.setTextColor(110, 110, 110);
			y = ensureSpace(doc, y, 8);
			doc.text(t("Egne billeder"), MARGIN, y);
			y = drawImages(doc, photos, y + 3, scale);
		}
		y += 1;
	}

	// --- Sted & link ---
	y = sectionLabel(doc, y, t("Sted & link"));
	if(poi.position) y = textBlock(doc, y, t("Position (x, y, z)"), `${poi.position.x.toFixed(2)}, ${poi.position.y.toFixed(2)}, ${poi.position.z.toFixed(2)}`);
	if(poi.pointcloudName) y = textBlock(doc, y, t("Punktsky"), poi.pointcloudName);
	if(poi.link) y = textBlock(doc, y, t("Link"), poi.link, {url: poi.link});
	if(poi.geonoteRef) y = textBlock(doc, y, "Geonote", poi.geonoteRef, /^https?:/i.test(poi.geonoteRef) ? {url: poi.geonoteRef} : {});

}

// Billedstørrelser, en POI prøves med: første forsøg i fuld størrelse, derefter mindre, til hele POI'en står på én side
const POI_SCALES = [1, 0.8, 0.65, 0.5, 0.4, 0.3, 0.22];

// Hver POI begynder på en ny side og holdes på den, hvis det kan lade sig gøre ved at gøre billederne mindre. Kan den ikke (meget lang
// beskrivelse), fortsætter den på næste side i mindste størrelse.
function drawPoiOnOwnPage(doc, poi, statuses, now){
	for(let i = 0; i < POI_SCALES.length; i++){
		doc.addPage();
		const page = doc.internal.getNumberOfPages();
		drawPoi(doc, poi, statuses, MARGIN + 6, now, POI_SCALES[i]);
		if(doc.internal.getNumberOfPages() === page || i === POI_SCALES.length - 1) return;
		for(let p = doc.internal.getNumberOfPages(); p >= page; p--) doc.deletePage(p); // for stort: tegn forfra med mindre billeder
	}
}

export function buildPoiReportPdf(pois, info = {}){
	const doc = new jsPDF({unit: "mm", format: "a4", orientation: "portrait"});
	const statuses = info.statuses || DEFAULT_STATUSES;
	const now = info.now ? new Date(info.now) : new Date();
	const list = (pois || []).slice();

	let y = pageHeader(doc, t("Inspektionsrapport"), info.name || t("Punktsky"), t("POI'er fra PointView"));

	const items = [
		[t("Genereret"), formatGeneratedAt(now)],
		[t("Antal POI'er"), String(list.length)],
		info.project ? [t("Projekt"), info.project] : null,
		info.coordinateSystem ? [t("Koordinatsystem"), info.coordinateSystem] : null,
		info.filterLabel ? [t("Udvalg"), info.filterLabel] : null,
	].filter(Boolean);
	const cols = 4, colWidth = CONTENT_WIDTH / cols;
	items.forEach(([label, value], i) => fieldLine(doc, MARGIN + (i % cols) * colWidth, y + Math.floor(i / cols) * 11, label, value, colWidth - 3));
	y += Math.ceil(items.length / cols) * 11 + 6;

	if(list.length === 0){
		doc.setFont(undefined, "normal");
		doc.setFontSize(10);
		doc.setTextColor(90, 90, 90);
		doc.text(t("Der er ingen POI'er i udvalget."), MARGIN, y);
	}else{
		y = drawOverview(doc, list, statuses, y, now) + 10;
		for(const poi of list) drawPoiOnOwnPage(doc, poi, statuses, now);
	}

	drawFooters(doc);

	const filename = poiReportFilename(info.name);
	if(info.output === "blob"){
		return {filename, blob: doc.output("blob"), pageCount: doc.internal.getNumberOfPages()}; // (til test/forhåndsvisning: gem ikke)
	}
	doc.save(filename);
	return {filename};
}
